'use client';

import React, { useState, useEffect, useRef } from 'react';

interface Question {
  id: string;
  question: string;
  lang: 'EN' | 'HI' | 'GU' | 'TA';
  langLabel: string;
  intent: string;
  sources: string[];
  thinking: string[];
  answer: string;
  kpis: { label: string; value: string; prefix?: string; suffix?: string; numericVal?: number }[];
  chips: string[];
  action: string;
  actionIcon: string;
}

const QUESTIONS: Question[] = [
  {
    id: 'q1',
    question: 'Which outlet is growing fastest?',
    lang: 'EN',
    langLabel: 'English',
    intent: 'outlet growth ranking',
    sources: ['Sales Data', 'POS Records', 'Revenue Ledger'],
    thinking: ['Scanning outlet-level revenue across Q1–Q2 2026…', 'Comparing growth rates across 6 locations…', 'Identifying top performer with statistical confidence…'],
    answer: 'Surat outlet leads with +22% revenue growth in Q2 2026. Ahmedabad follows at +14%. Vadodara shows steady +6% — driven by Staples category expansion.',
    kpis: [
      { label: 'Surat Growth', value: '+22%', numericVal: 22, suffix: '%', prefix: '+' },
      { label: 'Ahmedabad', value: '+14%', numericVal: 14, suffix: '%', prefix: '+' },
      { label: 'Outlets Analyzed', value: '6', numericVal: 6 },
    ],
    chips: ['Outlet Performance', 'Revenue Growth', 'Q2 2026', 'Regional Analysis'],
    action: 'View full outlet breakdown →',
    actionIcon: '📊',
  },
  {
    id: 'q2',
    question: 'Forecast cash flow for the next 30 days.',
    lang: 'EN',
    langLabel: 'English',
    intent: 'cash flow forecast',
    sources: ['Bank Ledger', 'Receivables', 'Purchase Orders', 'Payables'],
    thinking: ['Pulling confirmed POs and receivables pipeline…', 'Analyzing overdue payment patterns…', 'Running 30-day projection model…'],
    answer: 'Projected net cash: ₹2.4 Cr. Inflows of ₹68L expected from 14 confirmed POs. ₹3.4L in overdue payments flagged — Shanti Kirana (₹84,200) leads.',
    kpis: [
      { label: 'Net Cash Projected', value: '₹2.4 Cr', numericVal: 2.4, prefix: '₹', suffix: ' Cr' },
      { label: 'Expected Inflows', value: '₹68L', numericVal: 68, prefix: '₹', suffix: 'L' },
      { label: 'Overdue Flagged', value: '₹3.4L', numericVal: 3.4, prefix: '₹', suffix: 'L' },
    ],
    chips: ['Cash Flow', '30-Day Forecast', 'Receivables', 'Risk Flagged'],
    action: 'Generate cash flow report →',
    actionIcon: '💰',
  },
  {
    id: 'q3',
    question: 'Which products should I reorder this week?',
    lang: 'EN',
    langLabel: 'English',
    intent: 'inventory reorder alert',
    sources: ['Inventory System', 'Sales Velocity', 'Reorder Rules', 'Supplier Data'],
    thinking: ['Checking current stock levels against reorder thresholds…', 'Calculating days-to-stockout based on sales velocity…', 'Matching with preferred supplier availability…'],
    answer: '7 SKUs are below reorder level at Ahmedabad. Britannia Bourbon (142 units vs 500 reorder), Amul Butter (38 vs 120). Draft PO to Sunrise Distributors ready.',
    kpis: [
      { label: 'Critical SKUs', value: '7', numericVal: 7 },
      { label: 'Britannia Bourbon', value: '142 units', numericVal: 142, suffix: ' units' },
      { label: 'PO Ready', value: '1 draft', numericVal: 1, suffix: ' draft' },
    ],
    chips: ['Inventory Alert', 'Reorder Level', 'Ahmedabad', 'Purchase Order'],
    action: 'Draft purchase order →',
    actionIcon: '📦',
  },
  {
    id: 'q4',
    question: 'Show customers with overdue payments above ₹50,000.',
    lang: 'EN',
    langLabel: 'English',
    intent: 'overdue receivables filter',
    sources: ['CRM', 'Accounts Receivable', 'Payment History'],
    thinking: ['Filtering customer ledger for overdue balances > ₹50,000…', 'Calculating aging buckets and risk scores…', 'Ranking by exposure and days overdue…'],
    answer: '2 customers match: Shanti Kirana Stores — ₹84,200 (72 days overdue), Gokul Provision — ₹61,800 (88 days overdue). Total exposure: ₹1.46L.',
    kpis: [
      { label: 'Customers Flagged', value: '2', numericVal: 2 },
      { label: 'Total Exposure', value: '₹1.46L', numericVal: 1.46, prefix: '₹', suffix: 'L' },
      { label: 'Max Overdue Days', value: '88 days', numericVal: 88, suffix: ' days' },
    ],
    chips: ['Overdue Payments', 'CRM Filter', 'Risk Exposure', 'Collections'],
    action: 'Send payment reminders →',
    actionIcon: '⚠️',
  },
  {
    id: 'q5',
    question: 'Bhai konsa product fastest grow kar raha hai?',
    lang: 'HI',
    langLabel: 'Hinglish',
    intent: 'fastest growing product',
    sources: ['Sales Data', 'SKU Analytics', 'Revenue Trends'],
    thinking: ['Sabhi SKUs ka growth rate calculate kar raha hoon…', 'Q2 2026 mein top performers identify kar raha hoon…', 'Revenue aur units dono consider kar raha hoon…'],
    answer: 'Britannia Bourbon 100g sabse fast grow kar raha hai — Q2 mein +22% revenue, 6,201 units sold. Amul Salted Butter close second at +18%.',
    kpis: [
      { label: 'Top Product Growth', value: '+22%', numericVal: 22, suffix: '%', prefix: '+' },
      { label: 'Units Sold', value: '6,201', numericVal: 6201 },
      { label: 'Second Best', value: '+18%', numericVal: 18, suffix: '%', prefix: '+' },
    ],
    chips: ['Product Growth', 'Hinglish Query', 'SKU Ranking', 'Q2 2026'],
    action: 'Dekho full product report →',
    actionIcon: '🚀',
  },
  {
    id: 'q6',
    question: 'Agle month kis category pe focus karu?',
    lang: 'HI',
    langLabel: 'Hinglish',
    intent: 'category strategy recommendation',
    sources: ['Category Analytics', 'Margin Data', 'Sales Trends', 'Market Data'],
    thinking: ['Har category ka performance index calculate kar raha hoon…', 'Margin contribution aur growth potential dekh raha hoon…', 'Next month ke liye best opportunity identify kar raha hoon…'],
    answer: 'Snacks category highest index (100) aur fastest growth. Dairy bhi strong at 85. Beverages mein opportunity hai — abhi 50 index pe hai, margin potential high.',
    kpis: [
      { label: 'Snacks Index', value: '100', numericVal: 100 },
      { label: 'Dairy Index', value: '85', numericVal: 85 },
      { label: 'Beverages Potential', value: '50→', numericVal: 50 },
    ],
    chips: ['Category Strategy', 'Hinglish Query', 'Margin Analysis', 'Growth Opportunity'],
    action: 'Category expansion plan dekho →',
    actionIcon: '📈',
  },
  {
    id: 'q7',
    question: 'Stock kab refill karna chahiye?',
    lang: 'HI',
    langLabel: 'Hinglish',
    intent: 'stock replenishment timing',
    sources: ['Inventory Levels', 'Sales Velocity', 'Lead Times', 'Demand Forecast'],
    thinking: ['Current stock levels aur sales rate compare kar raha hoon…', 'Supplier lead times consider kar raha hoon…', 'Optimal reorder timing calculate kar raha hoon…'],
    answer: '3 products ko is hafte refill karna chahiye. Britannia Bourbon 4 din mein stockout hoga. Amul Butter 6 din. Recommended: aaj hi PO raise karo.',
    kpis: [
      { label: 'Urgent Refills', value: '3 items', numericVal: 3, suffix: ' items' },
      { label: 'Days to Stockout', value: '4 days', numericVal: 4, suffix: ' days' },
      { label: 'PO Value', value: '₹42K', numericVal: 42, prefix: '₹', suffix: 'K' },
    ],
    chips: ['Stock Alert', 'Hinglish Query', 'Replenishment', 'Urgent Action'],
    action: 'Abhi PO draft karo →',
    actionIcon: '⚡',
  },
];

const LANG_STYLES: Record<string, { bg: string; text: string; border: string }> = {
  EN: { bg: 'rgba(31,77,63,0.08)', text: '#1F4D3F', border: 'rgba(31,77,63,0.2)' },
  HI: { bg: 'rgba(139,105,20,0.08)', text: '#8B6914', border: 'rgba(139,105,20,0.2)' },
  GU: { bg: 'rgba(80,50,140,0.08)', text: '#50328C', border: 'rgba(80,50,140,0.2)' },
  TA: { bg: 'rgba(180,50,50,0.08)', text: '#B43232', border: 'rgba(180,50,50,0.2)' },
};

type Phase = 'idle' | 'intent' | 'sources' | 'thinking' | 'answer';

function useCountUp(target: number, active: boolean, duration = 1200) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active) { setVal(0); return; }
    const start = performance.now();
    const tick = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setVal(Math.round(eased * target * 10) / 10);
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [active, target, duration]);
  return val;
}

function KPICard({ kpi, active, delay }: { kpi: Question['kpis'][0]; active: boolean; delay: number }) {
  const num = useCountUp(kpi.numericVal ?? 0, active);
  const displayVal = kpi.numericVal !== undefined
    ? `${kpi.prefix ?? ''}${Number.isInteger(kpi.numericVal) ? Math.round(num) : num.toFixed(1)}${kpi.suffix ?? ''}`
    : kpi.value;

  return (
    <div
      style={{
        background: 'rgba(255,255,255,0.8)',
        border: '1px solid rgba(211,223,219,0.7)',
        borderRadius: '16px',
        padding: '14px 16px',
        opacity: active ? 1 : 0,
        transform: active ? 'translateY(0)' : 'translateY(8px)',
        transition: `opacity 0.4s ease ${delay}ms, transform 0.4s ease ${delay}ms`,
      }}
    >
      <p style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', color: '#7B8F88', letterSpacing: '0.04em', marginBottom: '4px', textTransform: 'uppercase' }}>
        {kpi.label}
      </p>
      <p style={{ fontFamily: 'var(--font-sans)', fontSize: '22px', fontWeight: 700, color: '#1F4D3F', letterSpacing: '-0.03em', lineHeight: 1 }}>
        {displayVal}
      </p>
    </div>
  );
}

function SourceChip({ source, active, delay }: { source: string; active: boolean; delay: number }) {
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '5px 10px',
        borderRadius: '20px',
        background: active ? 'rgba(31,77,63,0.1)' : 'rgba(211,223,219,0.3)',
        border: active ? '1px solid rgba(31,77,63,0.25)' : '1px solid rgba(211,223,219,0.5)',
        opacity: active ? 1 : 0.4,
        transform: active ? 'scale(1)' : 'scale(0.95)',
        transition: `all 0.35s ease ${delay}ms`,
      }}
    >
      <div style={{
        width: '6px', height: '6px', borderRadius: '50%',
        background: active ? '#1F4D3F' : '#9AADA7',
        animation: active ? 'pulse 1.5s ease-in-out infinite' : 'none',
      }} />
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', color: active ? '#1F4D3F' : '#7B8F88', letterSpacing: '0.04em' }}>
        {source}
      </span>
    </div>
  );
}

function ThinkingLine({ text, active, delay }: { text: string; active: boolean; delay: number }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: '8px',
      opacity: active ? 1 : 0,
      transform: active ? 'translateX(0)' : 'translateX(-8px)',
      transition: `opacity 0.4s ease ${delay}ms, transform 0.4s ease ${delay}ms`,
    }}>
      <div style={{
        width: '16px', height: '16px', borderRadius: '50%', flexShrink: 0, marginTop: '1px',
        background: 'rgba(31,77,63,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#1F4D3F' }} />
      </div>
      <p style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', color: '#4F665F', lineHeight: 1.6, letterSpacing: '0.02em' }}>
        {text}
      </p>
    </div>
  );
}

export default function AskVina() {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>('idle');
  const [visibleThinking, setVisibleThinking] = useState(0);
  const [answerChars, setAnswerChars] = useState(0);
  const phaseRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const active = QUESTIONS.find(q => q.id === activeId);

  const clearTimers = () => {
    phaseRef.current.forEach(t => clearTimeout(t));
    phaseRef.current = [];
  };

  const runSequence = (q: Question) => {
    clearTimers();
    setPhase('intent');
    setVisibleThinking(0);
    setAnswerChars(0);

    const t1 = setTimeout(() => setPhase('sources'), 900);
    const t2 = setTimeout(() => {
      setPhase('thinking');
      // reveal thinking lines one by one
      q.thinking.forEach((_, i) => {
        const t = setTimeout(() => setVisibleThinking(i + 1), i * 600);
        phaseRef.current.push(t);
      });
    }, 1800);
    const t3 = setTimeout(() => {
      setPhase('answer');
      // typewriter for answer
      let i = 0;
      const typeInterval = setInterval(() => {
        i += 3;
        setAnswerChars(i);
        if (i >= q.answer.length) clearInterval(typeInterval);
      }, 18);
    }, 1800 + q.thinking.length * 600 + 400);

    phaseRef.current.push(t1, t2, t3);
  };

  const handleSelect = (q: Question) => {
    if (activeId === q.id) {
      setActiveId(null);
      setPhase('idle');
      clearTimers();
      return;
    }
    setActiveId(q.id);
    runSequence(q);
  };

  useEffect(() => () => clearTimers(), []);

  return (
    <section
      id="ask"
      className="relative py-20 overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #F6F5F0 0%, #FBFAF6 100%)' }}
      aria-label="What can you ask VINA"
    >
      {/* Ambient */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute bottom-0 left-1/4 w-[500px] h-[300px] opacity-20"
          style={{ background: 'radial-gradient(ellipse, rgba(31,77,63,0.08) 0%, transparent 70%)', filter: 'blur(50px)' }} />
        <div className="absolute top-1/3 right-0 w-[400px] h-[400px] opacity-10"
          style={{ background: 'radial-gradient(ellipse, rgba(58,125,107,0.1) 0%, transparent 70%)', filter: 'blur(60px)' }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 style={{ fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 'clamp(2rem, 4vw, 3rem)', letterSpacing: '-0.03em', color: '#0D2620', lineHeight: 1.1, marginBottom: '16px' }}>
            Ask. VINA understands.
          </h2>
          <p style={{ fontFamily: 'var(--font-sans)', fontSize: '1.1rem', color: '#3D5550', maxWidth: '480px', margin: '0 auto', lineHeight: 1.65 }}>
            No dashboards to dig through.
            <br />
            No reports to decode.
            <br />
            Just ask.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 items-start">
          {/* Question cards — 2 cols */}
          <div className="lg:col-span-2 flex flex-col gap-2.5">
            {QUESTIONS.map((q) => {
              const isActive = activeId === q.id;
              const ls = LANG_STYLES[q.lang] || LANG_STYLES.EN;
              return (
                <button
                  key={q.id}
                  onClick={() => handleSelect(q)}
                  className="w-full text-left px-4 py-3.5 rounded-2xl"
                  style={{
                    background: isActive ? 'rgba(255,255,255,0.97)' : 'rgba(255,255,255,0.6)',
                    border: isActive ? '1px solid rgba(31,77,63,0.3)' : '1px solid rgba(211,223,219,0.55)',
                    boxShadow: isActive ? '0 8px 32px rgba(31,77,63,0.12)' : 'none',
                    transform: isActive ? 'translateX(4px)' : 'translateX(0)',
                    transition: 'all 0.3s cubic-bezier(0.22,1,0.36,1)',
                  }}
                  aria-expanded={isActive}
                  aria-label={`Ask VINA: ${q.question}`}
                >
                  <div className="flex items-start gap-2.5">
                    <span
                      className="flex-shrink-0 px-2 py-0.5 rounded-full text-xs"
                      style={{ background: ls.bg, color: ls.text, border: `1px solid ${ls.border}`, fontFamily: 'var(--font-mono)', fontSize: '9px', letterSpacing: '0.05em', marginTop: '2px' }}
                    >
                      {q.langLabel}
                    </span>
                    <p style={{ fontFamily: 'var(--font-sans)', fontSize: '13.5px', color: isActive ? '#0D2620' : '#3D5550', lineHeight: 1.5, fontWeight: isActive ? 500 : 400, flex: 1 }}>
                      {q.question}
                    </p>
                    <div className="flex-shrink-0 mt-0.5">
                      {isActive ? (
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#1F4D3F', animation: 'pulse 1.5s ease-in-out infinite' }} />
                      ) : (
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                          <path d="M4 2.5L8 6L4 9.5" stroke="#9AADA7" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Response panel — 3 cols */}
          <div className="lg:col-span-3 lg:sticky lg:top-24">
            {!active || phase === 'idle' ? (
              <div className="rounded-3xl p-10 text-center flex flex-col items-center justify-center"
                style={{ background: 'rgba(255,255,255,0.4)', border: '1px dashed rgba(211,223,219,0.7)', minHeight: '420px' }}>
                <div className="w-14 h-14 rounded-full flex items-center justify-center mb-5"
                  style={{ background: 'rgba(31,77,63,0.06)' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle cx="12" cy="12" r="9" stroke="#1F4D3F" strokeWidth="1.3" />
                    <path d="M8 12h8M12 8v8" stroke="#1F4D3F" strokeWidth="1.4" strokeLinecap="round" />
                  </svg>
                </div>
                <p style={{ fontFamily: 'var(--font-sans)', fontSize: '16px', color: '#7B8F88', lineHeight: 1.6, maxWidth: '260px' }}>
                  Select a question to watch VINA think in real time.
                </p>
                <div className="flex gap-2 mt-6 flex-wrap justify-center">
                  {['English', 'Hinglish', 'Regional'].map(l => (
                    <span key={l} style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', color: '#9AADA7', padding: '4px 10px', borderRadius: '20px', border: '1px solid rgba(211,223,219,0.6)', letterSpacing: '0.04em' }}>
                      {l}
                    </span>
                  ))}
                </div>
              </div>
            ) : (
              <div
                key={active.id}
                className="rounded-3xl overflow-hidden"
                style={{
                  background: 'rgba(255,255,255,0.92)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(211,223,219,0.8)',
                  boxShadow: '0 24px 60px rgba(31,77,63,0.1)',
                  animation: 'fadeSlideUp 0.35s cubic-bezier(0.22,1,0.36,1) both',
                }}
              >
                {/* Top bar */}
                <div className="px-6 py-4 flex items-center gap-3" style={{ borderBottom: '1px solid rgba(211,223,219,0.5)' }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#1F4D3F', animation: 'pulse 1.5s ease-in-out infinite' }} />
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', color: '#7B8F88', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                    vina · live analysis
                  </span>
                  <div className="ml-auto flex items-center gap-1.5">
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: '9px', color: '#9AADA7', letterSpacing: '0.04em' }}>
                      {phase === 'intent' ? 'detecting intent…' : phase === 'sources' ? 'scanning sources…' : phase === 'thinking' ? 'analyzing…' : 'complete'}
                    </span>
                    {phase !== 'answer' && (
                      <div style={{ display: 'flex', gap: '3px' }}>
                        {[0, 1, 2].map(i => (
                          <div key={i} style={{ width: '4px', height: '4px', borderRadius: '50%', background: '#1F4D3F', animation: `bounce 1s ease-in-out ${i * 0.15}s infinite` }} />
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Question echo */}
                <div className="px-6 pt-4 pb-3">
                  <p style={{ fontFamily: 'var(--font-sans)', fontSize: '13px', color: '#7B8F88', fontStyle: 'italic' }}>
                    "{active.question}"
                  </p>
                </div>

                {/* Phase 1: Intent detection */}
                <div className="px-6 pb-4">
                  <div
                    style={{
                      padding: '10px 14px',
                      borderRadius: '12px',
                      background: 'rgba(31,77,63,0.06)',
                      border: '1px solid rgba(31,77,63,0.12)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      opacity: phase !== 'idle' ? 1 : 0,
                      transform: phase !== 'idle' ? 'translateY(0)' : 'translateY(6px)',
                      transition: 'opacity 0.4s ease, transform 0.4s ease',
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                      <circle cx="7" cy="7" r="5.5" stroke="#1F4D3F" strokeWidth="1.1" />
                      <path d="M4.5 7L6.5 9L9.5 5" stroke="#1F4D3F" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', color: '#4F665F', letterSpacing: '0.03em' }}>
                      Intent detected:&nbsp;
                    </span>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', color: '#1F4D3F', fontWeight: 600, letterSpacing: '0.03em' }}>
                      {active.intent}
                    </span>
                  </div>
                </div>

                {/* Phase 2: Data sources */}
                <div className="px-6 pb-4">
                  <p style={{ fontFamily: 'var(--font-mono)', fontSize: '9px', color: '#9AADA7', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '8px' }}>
                    Analyzing sources
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {active.sources.map((src, i) => (
                      <SourceChip
                        key={src}
                        source={src}
                        active={phase === 'sources' || phase === 'thinking' || phase === 'answer'}
                        delay={i * 120}
                      />
                    ))}
                  </div>
                </div>

                {/* Phase 3: Thinking sequence */}
                {(phase === 'thinking' || phase === 'answer') && (
                  <div className="px-6 pb-4">
                    <p style={{ fontFamily: 'var(--font-mono)', fontSize: '9px', color: '#9AADA7', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '10px' }}>
                      Thinking
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {active.thinking.map((t, i) => (
                        <ThinkingLine key={i} text={t} active={visibleThinking > i} delay={0} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Phase 4: Answer */}
                {phase === 'answer' && (
                  <>
                    <div className="px-6 pb-4" style={{ borderTop: '1px solid rgba(211,223,219,0.4)', paddingTop: '16px', marginTop: '4px' }}>
                      <p style={{ fontFamily: 'var(--font-sans)', fontSize: '15px', color: '#1A3530', lineHeight: 1.75 }}>
                        {active.answer.slice(0, answerChars)}
                        {answerChars < active.answer.length && (
                          <span style={{ display: 'inline-block', width: '2px', height: '14px', background: '#1F4D3F', marginLeft: '2px', animation: 'blink 0.7s step-end infinite', verticalAlign: 'text-bottom' }} />
                        )}
                      </p>
                    </div>

                    {/* KPIs */}
                    <div className="px-6 pb-4">
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                        {active.kpis.map((kpi, i) => (
                          <KPICard key={kpi.label} kpi={kpi} active={answerChars > 20} delay={i * 150} />
                        ))}
                      </div>
                    </div>

                    {/* Floating context chips */}
                    <div className="px-6 pb-4">
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {active.chips.map((chip, i) => (
                          <span
                            key={chip}
                            style={{
                              fontFamily: 'var(--font-mono)',
                              fontSize: '10px',
                              color: '#1F4D3F',
                              padding: '4px 10px',
                              borderRadius: '20px',
                              background: 'rgba(31,77,63,0.07)',
                              border: '1px solid rgba(31,77,63,0.15)',
                              letterSpacing: '0.03em',
                              opacity: answerChars > 40 ? 1 : 0,
                              transform: answerChars > 40 ? 'translateY(0)' : 'translateY(4px)',
                              transition: `opacity 0.35s ease ${i * 80}ms, transform 0.35s ease ${i * 80}ms`,
                            }}
                          >
                            {chip}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Recommended action */}
                    <div className="px-6 pb-5">
                      <div
                        style={{
                          padding: '12px 16px',
                          borderRadius: '14px',
                          background: 'linear-gradient(135deg, rgba(31,77,63,0.08) 0%, rgba(58,125,107,0.06) 100%)',
                          border: '1px solid rgba(31,77,63,0.18)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          opacity: answerChars > 60 ? 1 : 0,
                          transform: answerChars > 60 ? 'translateY(0)' : 'translateY(6px)',
                          transition: 'opacity 0.4s ease 200ms, transform 0.4s ease 200ms',
                        }}
                      >
                        <span style={{ fontSize: '16px' }}>{active.actionIcon}</span>
                        <span style={{ fontFamily: 'var(--font-sans)', fontSize: '13px', color: '#1F4D3F', fontWeight: 500 }}>
                          {active.action}
                        </span>
                      </div>
                    </div>

                    {/* Source attribution */}
                    <div className="px-6 pb-4">
                      <p style={{ fontFamily: 'var(--font-mono)', fontSize: '9px', color: '#9AADA7', letterSpacing: '0.04em' }}>
                        Sources: {active.sources.join(' · ')}
                      </p>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.35; }
        }
        @keyframes bounce {
          0%, 100% { transform: translateY(0); opacity: 0.5; }
          50% { transform: translateY(-3px); opacity: 1; }
        }
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
      `}</style>
    </section>
  );
}
